import {Component, EventEmitter, Input, OnDestroy, OnInit, Output} from '@angular/core';
import {FormGroup} from '@angular/forms';
import {Subject} from 'rxjs';
import {takeUntil} from 'rxjs/operators';
import {QuestionBase} from '../models/question-base';
import {QuestionControlService} from '../services/question-control.service';
import {AdvFormService} from '../services/adv-form.service';
import {ApiUrl} from '../../../shared/models/apiurl.models';
import {SectionUI} from '../dynamic-form/dynamic-form.component';
import {HttpClient} from '@angular/common/http';
import {isSuppressedBy} from '../models/required-unless';

interface AdalineHistoryItem {
  key: string;
  question: string;
  answer: string;
}

@Component({
  selector: 'app-adaline-publish-form',
  templateUrl: './adaline-publish-form.component.html',
  styleUrls: ['./adaline-publish-form.component.scss'],
  providers: [QuestionControlService],
  standalone: false
})
export class AdalinePublishFormComponent implements OnInit, OnDestroy {
  @Input() questions: QuestionBase<string>[] = [];
  @Input() categoryLabel = '';
  @Output() requestManual = new EventEmitter<void>();

  form!: FormGroup;
  orderedQuestions: QuestionBase<string>[] = [];
  completionQuestions: QuestionBase<string>[] = [];
  currentIndex = 0;
  validationVisible = false;
  isComplete = false;
  isThinking = false;

  private readonly destroy$ = new Subject<void>();
  private autoAdvanceTimer?: number;
  private readonly autoAdvanceControls = new Set([
    'dropdown',
    'bigselectbox',
    'selectrelateddescription',
    'checkbox'
  ]);

  // SCRUM-154: option count per bindSelect list URL (e.g. models of the
  // selected manufacturer) — a list with no options is skipped, not asked.
  private readonly bindSelectOptionCount = new Map<string, number>();

  constructor(
    private qcs: QuestionControlService,
    private formService: AdvFormService,
    private apiUrl: ApiUrl,
    private http: HttpClient
  ) {}

  ngOnInit(): void {
    this.hydrateQuestionsFromSharedState();
    this.refreshQuestionAvailability();
    this.form = this.qcs.toFormGroup(this.questions || []);
    this.hydrateFormFromSharedState();
    this.isComplete = this.orderedQuestions.length === 0 ||
      this.orderedQuestions.every(question => this.isStepAnswered(question));
    this.currentIndex = this.isComplete ? this.orderedQuestions.length : this.findSharedResumeIndex();
    if (!this.isComplete) {
      this.formService.setActiveQuestion(this.currentQuestion?.key ?? null);
    }

    this.formService.getQOClickEvent()
      .pipe(takeUntil(this.destroy$))
      .subscribe(event => this.onSharedAnswerChanged(event));
  }

  ngOnDestroy(): void {
    window.clearTimeout(this.autoAdvanceTimer);
    this.destroy$.next();
    this.destroy$.complete();
  }

  get currentQuestion(): QuestionBase<string> | null {
    return this.isComplete ? null : (this.orderedQuestions[this.currentIndex] ?? null);
  }

  get progressPercent(): number {
    if (!this.orderedQuestions.length) return 100;
    if (this.isComplete) return 100;
    return Math.round(((this.currentIndex + 1) / this.orderedQuestions.length) * 100);
  }

  get sectionLabel(): string {
    const section = this.currentQuestion?.section as any;
    return section?.description || 'פרטי המודעה';
  }

  get history(): AdalineHistoryItem[] {
    return this.orderedQuestions
      .slice(0, this.currentIndex)
      .map(question => ({
        key: question.key,
        question: this.getStepLabel(question),
        answer: this.formatStepAnswer(question)
      }))
      .filter(item => item.answer)
      .slice(-4);
  }

  get canContinue(): boolean {
    const question = this.currentQuestion;
    return !question || !this.isStepRequired(question) || this.isStepAnswered(question);
  }

  get currentHasAnswer(): boolean {
    return this.currentQuestion ? this.isStepAnswered(this.currentQuestion) : true;
  }

  get requiredComplete(): boolean {
    return this.completionQuestions
      .filter(question => question.required)
      .every(question => this.hasAnswer(question));
  }

  isCarIdentificationQuestion(question: QuestionBase<string>): boolean {
    const section = question.section as any;
    return section?.namespace === 'CAR' && Number(section?.number) === 22;
  }

  getCarSectionQuestions(question: QuestionBase<string>): QuestionBase<string>[] {
    const section = question.section as any;
    return this.completionQuestions.filter(candidate => {
      const candidateSection = candidate.section as any;
      return candidateSection?.namespace === section?.namespace &&
        Number(candidateSection?.number) === Number(section?.number);
    });
  }

  getCarSection(question: QuestionBase<string>): SectionUI {
    const section = question.section as any;
    const sectionQuestions = this.getCarSectionQuestions(question);
    return {
      description: section?.description || 'סוג רכב',
      number: Number(section?.number ?? 22),
      namespace: section?.namespace || 'CAR',
      sectionKey: section?.sectionKey || 'car-identification',
      expanded: true,
      overflow: false,
      callBackFunction: section?.callBackFunction || '',
      questions: sectionQuestions as QuestionBase<String>[],
      requiredQuestion: sectionQuestions.filter(item => item.required)
    };
  }

  getStepLabel(question: QuestionBase<string>): string {
    if (!this.isCarIdentificationQuestion(question)) return question.label;
    return (question.section as any)?.description || 'זיהוי הרכב';
  }

  next(): void {
    window.clearTimeout(this.autoAdvanceTimer);
    this.isThinking = false;
    const question = this.currentQuestion;
    if (!question) return;

    if (!this.isCarIdentificationQuestion(question)) this.persistNativeControlValue(question);
    if (this.isStepRequired(question) && !this.isStepAnswered(question)) {
      this.validationVisible = true;
      if (this.isCarIdentificationQuestion(question)) {
        this.getCarSectionQuestions(question).forEach(item => this.form.get(item.key)?.markAsTouched());
      } else {
        this.form.get(question.key)?.markAsTouched();
      }
      return;
    }

    this.validationVisible = false;
    this.moveTo(this.currentIndex + 1);
  }

  previous(): void {
    if (this.currentIndex <= 0) return;
    window.clearTimeout(this.autoAdvanceTimer);
    this.isThinking = false;
    this.validationVisible = false;
    this.isComplete = false;
    this.moveTo(this.currentIndex - 1);
  }

  editHistory(item: AdalineHistoryItem): void {
    const index = this.orderedQuestions.findIndex(question => question.key === item.key);
    if (index < 0) return;
    window.clearTimeout(this.autoAdvanceTimer);
    this.isThinking = false;
    this.validationVisible = false;
    this.isComplete = false;
    this.moveTo(index);
  }

  switchToManual(): void {
    this.formService.setActiveQuestion(this.currentQuestion?.key ?? null);
    this.requestManual.emit();
  }

  trackHistory(_index: number, item: AdalineHistoryItem): string {
    return item.key;
  }

  private onSharedAnswerChanged(event: any): void {
    if (!event?.index) return;
    const currentKey = this.currentQuestion?.key;
    const question = this.questions.find(item => item.key === event.index);
    const value = this.formService.getQuestion(event.index);

    if (question) {
      question.value = value;
      this.form.get(question.key)?.setValue(value ?? '', {emitEvent: false});
    }

    this.refreshQuestionAvailability(currentKey);

    const current = this.currentQuestion;
    if (current && this.isCarIdentificationQuestion(current)) return;
    if (!current || event.index !== current.key || !this.hasAnswer(current)) return;
    if (!this.autoAdvanceControls.has(this.normalizeControlType(current.controlType))) return;
    if (current.type === 'multiSelect') return;

    window.clearTimeout(this.autoAdvanceTimer);
    this.isThinking = true;
    this.autoAdvanceTimer = window.setTimeout(() => {
      if (this.currentQuestion?.key === current.key) this.next();
      else this.isThinking = false;
    }, 1250);
  }

  private moveTo(index: number): void {
    window.clearTimeout(this.autoAdvanceTimer);
    if (index >= this.orderedQuestions.length) {
      this.isComplete = true;
      this.currentIndex = this.orderedQuestions.length;
      window.setTimeout(() => {
        document.querySelector<HTMLElement>('.adaline-completion')?.scrollIntoView({
          behavior: 'smooth',
          block: 'start'
        });
      });
      return;
    }

    this.currentIndex = Math.max(0, index);
    this.isComplete = false;
    this.formService.setActiveQuestion(this.currentQuestion?.key ?? null);
    window.setTimeout(() => {
      const surface = document.querySelector<HTMLElement>('.adaline-current-control');
      surface?.scrollIntoView({behavior: 'smooth', block: 'center'});
      const controlType = this.currentQuestion
        ? this.normalizeControlType(this.currentQuestion.controlType)
        : '';
      if (controlType !== 'dropdown') {
        surface?.querySelector<HTMLElement>('input:not([disabled]), textarea:not([disabled]), button:not([disabled])')
          ?.focus({preventScroll: true});
      }
    }, 80);
  }

  private findResumeIndex(): number {
    const requiredMissing = this.orderedQuestions.findIndex(
      question => this.isStepRequired(question) && !this.isStepAnswered(question)
    );
    if (requiredMissing >= 0) return requiredMissing;

    const firstMissing = this.orderedQuestions.findIndex(question => !this.isStepAnswered(question));
    return firstMissing >= 0 ? firstMissing : Math.max(0, this.orderedQuestions.length - 1);
  }

  private findSharedResumeIndex(): number {
    const activeKey = this.formService.getActiveQuestion();
    const activeIndex = activeKey
      ? this.orderedQuestions.findIndex(question => question.key === activeKey)
      : -1;

    if (activeIndex < 0) return this.findResumeIndex();
    if (!this.isStepAnswered(this.orderedQuestions[activeIndex])) return activeIndex;

    const nextIndex = activeIndex + 1;
    return nextIndex < this.orderedQuestions.length ? nextIndex : this.findResumeIndex();
  }

  private refreshQuestionAvailability(preferredKey?: string): void {
    const previousIndex = this.currentIndex;
    const visibleQuestions = [...(this.questions || [])]
      .filter(question => !question.disabled)
      .filter(question => this.isQuestionAvailable(question))
      .sort((a, b) => {
        const pageDifference = (a.page ?? 0) - (b.page ?? 0);
        return pageDifference || (a.order ?? 0) - (b.order ?? 0);
      });

    this.completionQuestions = visibleQuestions;
    const representedFeatureSections = new Set<string>();
    this.orderedQuestions = visibleQuestions.filter(question => {
      if (!this.isCarIdentificationQuestion(question)) return true;
      const section = question.section as any;
      const featureKey = `${section?.namespace}:${section?.number}`;
      if (representedFeatureSections.has(featureKey)) return false;
      representedFeatureSections.add(featureKey);
      return true;
    });

    if (preferredKey) {
      const preferredIndex = this.orderedQuestions.findIndex(question => question.key === preferredKey);
      if (preferredIndex >= 0) this.currentIndex = preferredIndex;
      else this.currentIndex = Math.min(previousIndex, Math.max(0, this.orderedQuestions.length - 1));
    }
  }

  private isQuestionAvailable(question: QuestionBase<string>): boolean {
    // SCRUM-154: e.g. "Test until" is skipped once "No test" is ticked.
    if (this.isSuppressed(question)) return false;
    if (question.type !== 'bindSelect') return true;

    try {
      const translated = this.apiUrl.pathTranslate(
        question.callBackFunction,
        this.formService,
        question.additionalParam,
        question
      );
      const available = translated.requiredField.every(field =>
        field.value !== undefined && field.value !== null && field.value !== ''
      );
      if (!available) {
        question.bindSelectHide = false;
        return false;
      }
      // SCRUM-154: only ask when the dependent list actually has options.
      const optionCount = this.bindSelectOptionCount.get(translated.path);
      if (optionCount === undefined) {
        this.bindSelectOptionCount.set(translated.path, -1);  // loading
        this.http.get<any[]>(translated.path).subscribe({
          next: options => {
            this.bindSelectOptionCount.set(translated.path, Array.isArray(options) ? options.length : 0);
            this.refreshQuestionAvailability(this.currentQuestion?.key);
          },
          error: () => this.bindSelectOptionCount.delete(translated.path)
        });
      }
      const hasOptions = optionCount === undefined || optionCount !== 0;
      question.bindSelectHide = hasOptions;
      return hasOptions;
    } catch {
      question.bindSelectHide = false;
      return false;
    }
  }

  private isSuppressed(question: QuestionBase<string>): boolean {
    return isSuppressedBy(question?.requiredUnless, key => this.formService.getQuestion(key));
  }

  private hydrateQuestionsFromSharedState(): void {
    this.questions.forEach(question => {
      const storedValue = this.formService.getQuestion(question.key);
      if (storedValue !== undefined) question.value = storedValue;
    });
  }

  private hydrateFormFromSharedState(): void {
    this.questions.forEach(question => {
      const storedValue = this.formService.getQuestion(question.key);
      if (storedValue !== undefined) {
        this.form.get(question.key)?.setValue(storedValue, {emitEvent: false});
      }
    });
  }

  private persistNativeControlValue(question: QuestionBase<string>): void {
    if (this.formService.getQuestion(question.key) !== undefined) return;
    const value = this.form.get(question.key)?.value;
    if (!this.isEmpty(value)) this.formService.addQuestion(question.key, value);
  }

  private hasAnswer(question: QuestionBase<string>): boolean {
    if (question.key === 'userContactDetails') return this.formService.isContactComplete();
    const sharedValue = this.formService.getQuestion(question.key);
    const value = sharedValue !== undefined ? sharedValue : this.form?.get(question.key)?.value;
    return !this.isEmpty(value);
  }

  private isStepRequired(question: QuestionBase<string>): boolean {
    return this.isCarIdentificationQuestion(question)
      ? this.getCarSectionQuestions(question).some(item => item.required)
      : question.required;
  }

  private isStepAnswered(question: QuestionBase<string>): boolean {
    if (!this.isCarIdentificationQuestion(question)) return this.hasAnswer(question);
    const sectionQuestions = this.getCarSectionQuestions(question);
    const requiredQuestions = sectionQuestions.filter(item => item.required);
    return requiredQuestions.length
      ? requiredQuestions.every(item => this.hasAnswer(item))
      : sectionQuestions.some(item => this.hasAnswer(item));
  }

  private formatStepAnswer(question: QuestionBase<string>): string {
    if (!this.isCarIdentificationQuestion(question)) {
      return this.formatAnswer(this.formService.getQuestion(question.key));
    }

    const values = this.getCarSectionQuestions(question)
      .map(item => this.formatAnswer(this.formService.getQuestion(item.key)))
      .filter((value, index, all) => Boolean(value) && all.indexOf(value) === index);
    return values.slice(0, 4).join(' · ');
  }

  private isEmpty(value: any): boolean {
    if (value === undefined || value === null || value === '') return true;
    if (Array.isArray(value)) return value.length === 0;
    return false;
  }

  private normalizeControlType(controlType: string): string {
    const normalized = (controlType || '').toLowerCase();
    if (normalized === 'selectfield' || normalized === 'select') return 'dropdown';
    if (normalized === 'selectbigbox') return 'bigselectbox';
    return normalized;
  }

  private formatAnswer(answer: any): string {
    if (answer === undefined || answer === null || answer === '') return '';
    if (Array.isArray(answer)) {
      return answer.map(item => this.formatAnswer(item)).filter(Boolean).join(', ');
    }
    if (typeof answer === 'object') {
      return String(answer.label ?? answer.name ?? answer.value?.value ?? answer.value ?? '').trim();
    }
    if (typeof answer === 'boolean') return answer ? 'כן' : 'לא';
    return String(answer).trim();
  }
}
